import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { LoadingCircle } from 'hosman-material';
import QUARTERS from '../../eugene/api/ressources/App/quarters';

const FILL_PAINT = { 'fill-color': 'hsl(235, 100%, 37%)', 'fill-opacity': 0.2 };
const LINE_PAINT = { 'line-color': 'hsl(235, 100%, 37%)', 'line-width': 2 };

const QuarterPolygonMap = ({ quarterId, center, zoom = 12, fixed = false }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [geojson, setGeojson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    QUARTERS.getQuarterGeojson(quarterId)
      .then(({ data }) => {
        if (!isMounted) return;
        setGeojson(data);
        setLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [quarterId]);

  useEffect(() => {
    if (!geojson || map.current) return;

    mapboxgl.accessToken = process.env.MAPBOX_PUBLIC_KEY;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: process.env.MAPBOX_STYLE,
      center: [center.lng, center.lat],
      zoom,
      dragPan: !fixed,
      scrollZoom: !fixed,
      doubleClickZoom: !fixed,
      keyboard: !fixed,
      touchZoomRotate: !fixed,
    });

    if (fixed) {
      map.current.getCanvas().style.cursor = 'default';
    }

    const markerDiv = document.createElement('div');
    markerDiv.className = `hosman-marker ${fixed ? 'cursor-default' : 'cursor-pointer'}`;
    new mapboxgl.Marker(markerDiv).setLngLat([center.lng, center.lat]).addTo(map.current);

    map.current.on('load', () => {
      if (map.current.getSource('quarter')) return;

      map.current.addSource('quarter', { type: 'geojson', data: geojson });
      map.current.addLayer({ id: 'quarter-fill', type: 'fill', source: 'quarter', paint: FILL_PAINT });
      map.current.addLayer({ id: 'quarter-line', type: 'line', source: 'quarter', paint: LINE_PAINT });
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, [center.lat, center.lng, fixed, geojson, zoom]);

  if (loading) {
    return (
      <div className='border border-ih-blue rounded-3xl h-96 flex items-center justify-center' data-testid='QuarterPolygonMap'>
        <LoadingCircle />
      </div>
    );
  }

  if (!geojson) return null;

  return (
    <div className='border border-ih-blue rounded-3xl' data-testid='QuarterPolygonMap'>
      <div ref={mapContainer} className='h-96 w-full cursor-pointer relative' />
    </div>
  );
};

export default QuarterPolygonMap;
